/**
 * MSW inline responses for /apollo/todos/*.
 *
 * Split out of the main inlineResponses.tsx so company-todo fixtures
 * can grow without pushing that file past the 400-line limit. These
 * are loaded representative shapes (overdue tint, AI-source link,
 * done-row Reopen path, cross-company assignee view) so the populated
 * story snapshots actually exercise each visual state instead of
 * showing an empty list.
 *
 * FIXED_DATE in storybook tests is 2025-01-15. Dates in 2026 render as
 * "in N months", dates in 2024 render as overdue.
 */
export const COMPANY_TODOS_INLINE_RESPONSES: Record<
  string,
  Record<string, unknown> | unknown[]
> = {
  // Recap-ready state so the Log Sales Call dialog stories render the
  // Dialpad status banner with a realistic AI recap.
  "/apollo/dialpad-calls/sales-call-live": {
    hasDialpadCall: true,
    callEnded: true,
    recapReady: true,
    actionItemsReceived: true,
    recapSummary:
      "Spoke with the shipping manager about weekly LTL out of New Jersey. They currently use a mix of carriers booked by phone and want online quoting. Next step: send pricing for their five most common lanes.",
  },
  "/apollo/todos/for-company": [
    {
      todoId: "ct-1111-0000-0000-000000000001",
      companyId: "11111111-1111-1111-1111-111111111111",
      title: "Send weekly pricing proposal",
      description: "Customer wants weekly LTL out of NJ; decision in 30 days.",
      assignee: "chris@freightsimple.com",
      status: "open",
      source: "manual",
      date: "2026-06-03",
      deadline: "2026-06-05",
      createdAt: "2026-05-20T14:00:00Z",
      createdBy: "chris@freightsimple.com",
    },
    {
      todoId: "ct-1111-0000-0000-000000000002",
      companyId: "11111111-1111-1111-1111-111111111111",
      title: "Schedule follow-up for next Tuesday",
      assignee: "chris@freightsimple.com",
      status: "open",
      source: "dialpad_recap",
      sourceCompanyNoteId: "note-aaaa-0000-0000-000000000001",
      createdAt: "2026-05-22T16:14:00Z",
      createdBy: "Dialpad",
    },
    {
      todoId: "ct-1111-0000-0000-000000000003",
      companyId: "11111111-1111-1111-1111-111111111111",
      title: "Chase missing W-9 for invoice payment",
      assignee: "edgardo.valencia@freightsimple.com",
      status: "open",
      source: "manual",
      deadline: "2024-12-15",
      createdAt: "2024-12-01T12:00:00Z",
      createdBy: "edgardo.valencia@freightsimple.com",
    },
    {
      todoId: "ct-1111-0000-0000-000000000004",
      companyId: "11111111-1111-1111-1111-111111111111",
      title: "Introduce customer success contact",
      assignee: "chris@freightsimple.com",
      status: "done",
      source: "dialpad_recap",
      sourceCompanyNoteId: "note-aaaa-0000-0000-000000000001",
      createdAt: "2026-05-19T11:30:00Z",
      completedAt: "2026-05-21T15:10:00Z",
      createdBy: "Dialpad",
      updatedBy: "chris@freightsimple.com",
    },
  ],
  "/apollo/todos/by-assignees": [
    {
      todo: {
        todoId: "ct-2222-0000-0000-000000000001",
        companyId: "11111111-1111-1111-1111-111111111111",
        title: "Send weekly pricing proposal",
        description: "Weekly LTL lane NJ → FL. Decision in 30 days.",
        assignee: "chris@freightsimple.com",
        status: "open",
        source: "manual",
        deadline: "2026-06-05",
        createdAt: "2026-05-20T14:00:00Z",
        createdBy: "chris@freightsimple.com",
      },
      companyName: "Acme Logistics",
      temperature: "Hot",
      shipmentFrequency: "Weekly",
    },
    {
      todo: {
        todoId: "ct-2222-0000-0000-000000000002",
        companyId: "22222222-2222-2222-2222-222222222222",
        title: "Send signed CSP agreement",
        assignee: "chris@freightsimple.com",
        status: "open",
        source: "dialpad_recap",
        sourceCompanyNoteId: "note-aaaa-0000-0000-000000000002",
        createdAt: "2026-05-23T09:14:00Z",
        createdBy: "Dialpad",
      },
      companyName: "Brampton Distribution",
      temperature: "Warm",
      shipmentFrequency: "Daily",
    },
    {
      todo: {
        todoId: "ct-2222-0000-0000-000000000003",
        companyId: "33333333-3333-3333-3333-333333333333",
        title: "Chase missing W-9 before next pickup",
        assignee: "chris@freightsimple.com",
        status: "open",
        source: "manual",
        deadline: "2024-12-15",
        createdAt: "2024-12-01T12:00:00Z",
        createdBy: "chris@freightsimple.com",
      },
      companyName: "Mississauga Freight",
      temperature: "Cold",
      shipmentFrequency: "Occasional",
    },
    {
      todo: {
        todoId: "ct-2222-0000-0000-000000000004",
        companyId: "11111111-1111-1111-1111-111111111111",
        title: "Resend pickup instructions PDF",
        assignee: "chris@freightsimple.com",
        status: "done",
        source: "manual",
        createdAt: "2026-05-18T08:00:00Z",
        completedAt: "2026-05-18T14:30:00Z",
        createdBy: "chris@freightsimple.com",
      },
      companyName: "Acme Logistics",
      temperature: "Hot",
      shipmentFrequency: "Weekly",
    },
    // Carrier-owned todo: the Company and Carrier columns are separate, so the
    // list needs one of each to show both populated.
    {
      todo: {
        todoId: "ct-2222-0000-0000-000000000005",
        carrierIdentifier: "saia",
        title: "Follow up on missed pickup window in Dallas",
        assignee: "chris@freightsimple.com",
        status: "open",
        source: "manual",
        deadline: "2026-06-12",
        createdAt: "2026-05-27T16:20:00Z",
        createdBy: "chris@freightsimple.com",
      },
      carrierIdentifier: "saia",
      carrierDisplayName: "Saia",
    },
  ],
  "/apollo/todos/counts-for-me": { openCount: 4, overdueCount: 1 },
};
